
import { supabase } from "@/integrations/supabase/client";
import { Message } from "../message-item";

/**
 * Gets the latest message content from a conversation
 */
const getLastMessageContent = (messages: Message[]): string | null => {
  if (messages.length === 0) return null;
  
  // Get the last non-empty message from assistant or user
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if ((message.role === 'assistant' || message.role === 'user') && message.content?.trim()) {
      return message.content.substring(0, 100); // Limit length for database storage
    }
  }
  
  return null;
};

/**
 * Generate a title for the conversation based on the first user message
 */
const generateConversationTitle = (messages: Message[]): string | null => {
  // Find the first non-welcome user message
  const firstUserMessage = messages.find(m => 
    m.role === 'user' && m.content?.trim() && !m.isWelcomeMessage
  );
  
  if (firstUserMessage?.content) {
    // Use the first few words as the title
    const words = firstUserMessage.content.split(' ');
    const titleWords = words.slice(0, 5).join(' ');
    return titleWords.length < 30 ? titleWords : titleWords.substring(0, 30) + '...';
  }
  
  return null;
};

/**
 * Test function to manually test database saving capabilities
 */
export const testDatabaseSaving = async (): Promise<boolean> => {
  console.log("🔧 [TEST] Starting manual database save test...");
  
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      console.error("❌ [TEST] Auth error:", userError);
      return false;
    }
    
    if (!user) {
      console.error("❌ [TEST] No authenticated user");
      return false;
    }
    
    console.log("✅ [TEST] User authenticated:", user.id);
    
    // Test inserting a simple conversation
    const testThreadId = `test_${Date.now()}`;
    const { data: insertData, error: insertError } = await supabase
      .from('chat_conversations')
      .insert({
        thread_id: testThreadId,
        user_id: user.id,
        title: 'Test Conversation',
        last_message: 'Testing database saving',
        updated_at: new Date().toISOString()
      })
      .select('id');
    
    if (insertError) {
      console.error("❌ [TEST] Insert error:", insertError);
      console.error("❌ [TEST] Error details:", JSON.stringify(insertError, null, 2));
      return false;
    }
    
    console.log("✅ [TEST] Successfully inserted test conversation:", insertData);
    
    // Clean up test data
    if (insertData && insertData.length > 0) {
      const { error: deleteError } = await supabase
        .from('chat_conversations')
        .delete()
        .eq('id', insertData[0].id);
      
      if (deleteError) {
        console.warn("⚠️ [TEST] Failed to clean up test data:", deleteError);
      } else {
        console.log("✅ [TEST] Test data cleaned up successfully");
      }
    }
    
    return true;
  } catch (error) {
    console.error("❌ [TEST] Exception during database test:", error);
    return false;
  }
};

/**
 * Enhanced conversation saving with comprehensive error handling
 */
export const saveConversationToDatabase = async (
  threadId: string,
  messages: Message[]
): Promise<boolean> => {
  if (!threadId) {
    console.log("❌ [CONVERSATION-DB] No threadId provided, cannot save conversation");
    return false;
  }

  try {
    console.log(`💾 [CONVERSATION-DB] ===== STARTING SAVE PROCESS =====`);
    console.log(`💾 [CONVERSATION-DB] Thread ID: "${threadId}"`);
    console.log(`💾 [CONVERSATION-DB] Messages count: ${messages.length}`);
    console.log(`💾 [CONVERSATION-DB] Messages structure:`, messages.map(m => ({
      id: m.id,
      role: m.role,
      contentLength: m.content?.length || 0,
      isWelcome: m.isWelcomeMessage
    })));
    
    // Test database connectivity first
    console.log(`💾 [CONVERSATION-DB] Testing database connectivity...`);
    const dbTestResult = await testDatabaseSaving();
    if (!dbTestResult) {
      console.error("❌ [CONVERSATION-DB] Database connectivity test failed");
      return false;
    }
    console.log("✅ [CONVERSATION-DB] Database connectivity test passed");
    
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      console.error("❌ [CONVERSATION-DB] User authentication error:", userError);
      console.error("❌ [CONVERSATION-DB] User error details:", JSON.stringify(userError, null, 2));
      return false;
    }
    
    if (!user) {
      console.error("❌ [CONVERSATION-DB] No user found, cannot save conversation");
      return false;
    }

    console.log(`💾 [CONVERSATION-DB] User authenticated successfully: ${user.id}`);
    console.log(`💾 [CONVERSATION-DB] User email: ${user.email}`);
    
    const title = generateConversationTitle(messages);
    const lastMessage = getLastMessageContent(messages);
    
    console.log("💾 [CONVERSATION-DB] Generated conversation data:");
    console.log(`💾 [CONVERSATION-DB] - Title: "${title}"`);
    console.log(`💾 [CONVERSATION-DB] - Last message: "${lastMessage?.substring(0, 50)}..."`);
    
    if (!title) {
      console.log("❌ [CONVERSATION-DB] No title generated - likely no user messages found");
      console.log("❌ [CONVERSATION-DB] User messages in conversation:", 
        messages.filter(m => m.role === 'user' && !m.isWelcomeMessage).length);
      return false;
    }
    
    // Prepare the data object
    const conversationData = {
      thread_id: threadId,
      user_id: user.id,
      updated_at: new Date().toISOString(),
      title: title,
      last_message: lastMessage
    };
    
    console.log(`💾 [CONVERSATION-DB] Prepared conversation data:`, conversationData);
    
    // Attempt the upsert operation
    console.log(`💾 [CONVERSATION-DB] Attempting database upsert...`);
    
    const { data, error } = await supabase
      .from('chat_conversations')
      .upsert(conversationData, {
        onConflict: 'thread_id,user_id',
        ignoreDuplicates: false
      })
      .select('id, thread_id, title');
    
    if (error) {
      console.error("❌ [CONVERSATION-DB] Database upsert error:", error);
      console.error("❌ [CONVERSATION-DB] Error code:", error.code);
      console.error("❌ [CONVERSATION-DB] Error message:", error.message);
      console.error("❌ [CONVERSATION-DB] Error details:", error.details);
      console.error("❌ [CONVERSATION-DB] Error hint:", error.hint);
      console.error("❌ [CONVERSATION-DB] Full error object:", JSON.stringify(error, null, 2));
      
      // Try to get more context about the error
      if (error.code === 'PGRST116') {
        console.error("❌ [CONVERSATION-DB] RLS Policy Error - checking user permissions...");
        
        // Test if we can read from the table
        const { data: testRead, error: readError } = await supabase
          .from('chat_conversations')
          .select('id')
          .limit(1);
        
        if (readError) {
          console.error("❌ [CONVERSATION-DB] Cannot read from table:", readError);
        } else {
          console.log("✅ [CONVERSATION-DB] Can read from table successfully");
        }
      }
      
      return false;
    }
    
    console.log("✅ [CONVERSATION-DB] Upsert operation successful!");
    console.log("✅ [CONVERSATION-DB] Returned data:", data);
    console.log(`💾 [CONVERSATION-DB] ===== SAVE PROCESS COMPLETED =====`);
    
    return true;
    
  } catch (err) {
    console.error("❌ [CONVERSATION-DB] Exception during save operation:", err);
    console.error("❌ [CONVERSATION-DB] Exception stack:", (err as Error).stack);
    return false;
  }
};

/**
 * Enhanced conversation deletion with better error handling
 */
export const deleteConversationData = async (threadId: string): Promise<boolean> => {
  if (!threadId) return true;

  try {
    console.log("🗑️ [CONVERSATION-DB] ===== STARTING DELETE PROCESS =====");
    console.log("🗑️ [CONVERSATION-DB] Thread ID:", threadId);
    
    // Delete from OpenAI API first
    try {
      console.log("🗑️ [CONVERSATION-DB] Deleting from OpenAI API...");
      await supabase.functions.invoke("openai-assistant", {
        body: { 
          action: "delete_thread",
          threadId: threadId
        }
      });
      console.log("✅ [CONVERSATION-DB] Thread deleted from OpenAI API");
    } catch (apiError) {
      console.warn("⚠️ [CONVERSATION-DB] Could not delete thread in OpenAI API:", apiError);
    }

    // Delete from database
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error("❌ [CONVERSATION-DB] User error during delete:", userError);
        return true; // Continue even if user lookup fails
      }
      
      if (!user) {
        console.log("ℹ️ [CONVERSATION-DB] No user found during delete");
        return true;
      }
      
      console.log("🗑️ [CONVERSATION-DB] Deleting from database...");
      const { error: deleteError } = await supabase
        .from('chat_conversations')
        .delete()
        .eq('thread_id', threadId)
        .eq('user_id', user.id);
        
      if (deleteError) {
        console.error("❌ [CONVERSATION-DB] Database delete error:", deleteError);
        return false;
      }
      
      console.log("✅ [CONVERSATION-DB] Successfully deleted from database");
    } catch (dbError) {
      console.warn("⚠️ [CONVERSATION-DB] Database deletion error:", dbError);
    }

    console.log("🗑️ [CONVERSATION-DB] ===== DELETE PROCESS COMPLETED =====");
    return true;
  } catch (error) {
    console.error("❌ [CONVERSATION-DB] Error deleting conversation:", error);
    return false;
  }
};
